import * as Sentry from "@sentry/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useTranslation, Trans } from "react-i18next";
import { Disclaimers } from "src/screens/auth/register/Disclaimers";
import styles from "./NoticeGateModal.module.css";

const CONSENT_KEY = "cookie_consent_v1";

const hasConsent = () => {
    try {
        return localStorage.getItem(CONSENT_KEY) === "true";
    } catch {
        return false;
    }
};

const NoticeGateModalBase = () => {
    const { t } = useTranslation();

    const [isVisible, setIsVisible] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(false);

    // compute once for the <Trans> slot
    const termsOfUse = useMemo(() => t("screens.auth.register.disclaimers"), [t]);

    // Show the modal if consent is missing
    useEffect(() => {
        setIsVisible(!hasConsent());
    }, []);

    useEffect(() => {
        if (isVisible) {
            document.body.style.overflow = "hidden";
            return;
        }

        // when modal is NOT visible, make sure scrolling is enabled again
        document.body.style.overflow = "";
    }, [isVisible]);

    const onShowDisclaimer = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        setShowDisclaimer(true);
    }, []);

    const onHideDisclaimer = useCallback(() => setShowDisclaimer(false), []);

    const onAccept = useCallback(() => {
        setShowDisclaimer(false);
        try {
            localStorage.setItem(CONSENT_KEY, "true");
        } catch {}

        setIsVisible(false);
        window.dispatchEvent(new Event("consent:accepted"));
    }, []);

    if (!isVisible) return null;

    return (
        <>
            <UI.Modal show={true} size="lg" centered={true}>
                <UI.Col className={styles.Container}>
                    <div className={styles.Title}>{t("cookie.title").toString()}</div>
                    <div className={styles.InfoText}>{t("cookie.info_text1")}</div>
                    <div className={styles.InfoText}>{t("cookie.info_text2")}</div>
                    <div className={styles.Actions}>
                        <UI.Button className={styles.Button} label={t("cookie.accept_cookies")} onClick={onAccept} />
                    </div>
                    <div className={styles.LinkText}>
                        <Trans i18nKey="cookie.link_text">
                            A <button onClick={onShowDisclaimer}>{{ termsOfUse }}</button> B
                        </Trans>
                    </div>
                </UI.Col>
            </UI.Modal>

            <UI.Modal
                title={t("screens.auth.register.disclaimers").toString()}
                show={showDisclaimer}
                onClose={onHideDisclaimer}
                size="lg"
            >
                <Disclaimers />
            </UI.Modal>
        </>
    );
};

export const NoticeGateModal = Sentry.withProfiler(observer(NoticeGateModalBase));
